import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import styled from "@mui/material/styles/styled";
import { useClinicState } from "../../../clinic/clinicStateContext";
import { useGetUsersOfClinic } from "../../../clinic/hooks/useGetUsersOfClinic";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { LoadingProgress } from "fe-web-components/loader/loadingProgress";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { GetUsersOfClinicUsers, UserRoles } from "fe-common/graphql";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { getClinicRoleTitle } from "./clinicUserRoles";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import {
  SectionBox,
  SectionTitle,
} from "../clinicCustomization/clinicCustomization";
import AddIcon from "@mui/icons-material/Add";
import { ReactComponent as DeleteIcon } from "../../../assets/clinic/deleteIcon.svg";
import { ReactComponent as EditIcon } from "../../../assets/clinic/editIcon.svg";
import { InfoBox } from "../elements/infoBox";
import { ClinicUserForm } from "./clinicUserForm";
import { useClinicMessagesContext } from "../clinicMessagesContext";

//region [[ Styles ]]
const ClinicUsersView = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  "& th": {
    paddingTop: 10,
    paddingBottom: 10,
  },
});

const UsersTable = styled(Table)({
  border: `1px solid ${ClinicColors.borders}`,
  margin: "20px 0",
});

const ClinicUsersFooter = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
});

const UserActionButton = styled(IconButton)({
  backgroundColor: ClinicColors.selectedBg,
  color: ClinicColors.textMain,
  padding: 8,
  marginLeft: 6,
  "& svg": {
    width: 16,
    height: 17,
  },
  "& path": {
    stroke: ClinicColors.textMain,
  },
  "&:disabled": {
    backgroundColor: ClinicColors.selectedBg,
    opacity: 0.5,
  },
});

//endregion [[ Styles ]]
//region [[ Props ]]
const dummyUser = { email: "new", role: UserRoles.KahunClinicManager };
//endregion [[ Props ]]
//region [[ Functions ]]

//endregion [[ Functions ]]

export const ClinicUsers = () => {
  const [userToUpdate, setUserToUpdate] = useState<GetUsersOfClinicUsers>(null);
  const [userToDelete, setUserToDelete] = useState<GetUsersOfClinicUsers>(null);
  const [deletingUser, setShowDeletingUser] = useState<boolean>(false);
  const dao = useClinicContext();
  const [
    {
      clinicData: { id: clinicId, name: clinicName },
    },
  ] = useClinicState();
  const { setMessage } = useClinicMessagesContext();
  const [clinicUsers, setClinicUsers] = useState<GetUsersOfClinicUsers[]>([]);

  const { loading, users, currentUser, isKahunUser } = useGetUsersOfClinic(
    clinicId,
  );

  useEffect(() => {
    // Don't let non @kahun.com users see there are support privileges for support@kahun.com
    setClinicUsers(
      users?.filter((u) => isKahunUser || !u.email?.includes("kahun.com")) ||
        [],
    );
  }, [users, isKahunUser]);

  const removeUser = useCallback(
    async (userToRemove) => {
      if (userToRemove.role === UserRoles.KahunClinicManager) {
        if (
          clinicUsers.filter((u) => u.role === UserRoles.KahunClinicManager)
            .length === 1
        ) {
          setMessage({
            text: `At least one clinic administrator is required`,
            severity: "error",
          });
          return;
        }
      }
      const userEmail = userToRemove.email.toLowerCase();
      setShowDeletingUser(true);
      try {
        const status = await dao.removeUserFromClinic(clinicId, userEmail);
        if (status === "success") {
          evt({
            category: CATEGORY.CLINIC_MANAGEMENT,
            action: ACTION.CLINIC_REMOVE_CLINIC_USER,
            props: {
              deletedUser: userEmail,
              clinicName,
            },
          });
          setClinicUsers((prevState) =>
            prevState.filter((u) => u.email !== userEmail),
          );
        }
        setMessage({
          text:
            status === "success"
              ? `${userEmail} removed successfully`
              : `${userEmail} couldn't be deleted`,
          severity: status === "success" ? "success" : "error",
        });
      } catch (err) {
        console.error(err);
        setMessage({
          text: `${userEmail} couldn't be deleted`,
          severity: "error",
        });
      } finally {
        setShowDeletingUser(false);
      }
    },
    [dao, clinicId, clinicUsers, clinicName, setMessage],
  );
  const handleCloseDeleteDialog = () => {
    setUserToDelete(null);
  };

  if (loading)
    return (
      <SectionBox>
        <LoadingProgress label={"Loading clinic members..."} />
      </SectionBox>
    );

  return (
    <SectionBox>
      <ClinicUsersView>
        <SectionTitle>Clinic Members</SectionTitle>
        <InfoBox>Manage clinic permissions, add or update members.</InfoBox>
        <UsersTable>
          <TableHead>
            <TableRow>
              <TableCell>Clinic Member</TableCell>
              <TableCell>Role</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clinicUsers
              .sort((a, b) => a.email.localeCompare(b.email))
              .map((user, index) => (
                <TableRow key={"user_row" + index}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {getClinicRoleTitle(
                      user.role || UserRoles.KahunClinicManager,
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <UserActionButton
                      onClick={() => setUserToUpdate(user)}
                      aria-label="edit member"
                      title="edit member"
                    >
                      <EditIcon />
                    </UserActionButton>
                    <UserActionButton
                      onClick={() => setUserToDelete(user)}
                      aria-label="remove member"
                      title="Remove member"
                      disabled={!isKahunUser && user.email === currentUser}
                    >
                      <DeleteIcon />
                    </UserActionButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </UsersTable>
        <ClinicUsersFooter>
          {deletingUser && <LoadingProgress label={"Removing a member"} />}
          <ClinicActionButton
            label={"ADD MEMBER"}
            endIcon={<AddIcon />}
            onClick={() => setUserToUpdate(dummyUser)}
            style={{ marginLeft: "auto" }}
          />
        </ClinicUsersFooter>
      </ClinicUsersView>

      {userToUpdate && (
        <ClinicUserForm
          onUpdated={(userData) => {
            setClinicUsers((prevState) => {
              const retVal = [
                ...prevState.filter((u) => u.email !== userData.email),
                { email: userData.email, role: userData.role },
              ];
              return retVal;
            }); //add user to list
          }}
          onClose={() => setUserToUpdate(null)}
          userToUpdate={userToUpdate}
          users={clinicUsers}
        />
      )}

      {userToDelete && (
        <Dialog open={true} onClose={handleCloseDeleteDialog}>
          <DialogContent>
            Are you sure you want to remove {userToDelete.email}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDeleteDialog}
              color="secondary"
              autoFocus
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                removeUser(userToDelete).then(() => {});
                handleCloseDeleteDialog();
              }}
              color="primary"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </SectionBox>
  );
};
